import React, { useState, useEffect } from "react"
import CountUp from "react-countup"
import { ArrowUp, ArrowDown } from "lucide-react"
import { computeProductConversion } from "../../../utils/productConversionHelpers"
import FunnelChart from "../../../components/ui/funnel-chart"

const CHART_COLOR_VARS = ["--chart-1", "--chart-2", "--chart-3", "--chart-4", "--chart-5"]
const colorFor = (i) => `var(${CHART_COLOR_VARS[i % CHART_COLOR_VARS.length]})`

const RANGE_OPTIONS = [
  { key: "7d", label: "Last 7 Days" },
  { key: "30d", label: "Last 30 Days" },
  { key: "90d", label: "Last 90 Days" },
]

const TrendBadge = ({ value }) => {
  if (value === null || value === undefined) return null
  const isUp = value >= 0
  return (
    <div
      className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
        isUp ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"
      }`}
    >
      {isUp ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
      {Math.abs(value)}%
    </div>
  )
}

const MetricRow = ({ label, value, change, index }) => (
  <div
    className="flex items-center justify-between py-3 border-b border-border last:border-0"
    style={{ animationDelay: `${index * 50}ms` }}
  >
    <span className="text-sm text-muted-foreground">{label}</span>
    <div className="flex items-center gap-2">
      <span className="text-lg font-semibold text-foreground tabular-nums">
        <CountUp end={value || 0} duration={1.2} separator="," />
      </span>
      <TrendBadge value={change} />
    </div>
  </div>
)

const ProductConversionSection = ({ orgId }) => {
  const [range, setRange] = useState("30d")
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!orgId) return
    setLoading(true)
    computeProductConversion(orgId, range).then((result) => {
      setData(result)
      setLoading(false)
    })
  }, [orgId, range])

  if (loading || !data) {
    return <div className="h-96 bg-muted rounded-2xl animate-pulse mb-6" />
  }

  if (!data?.hasPaidProducts) {
    return (
      <div className="bg-card border border-border rounded-2xl p-6 mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-1">Product Conversion</h3>
        <p className="text-sm text-muted-foreground">
          This shows the commerce funnel for paid products. Once you have at least one paid course or program, this section will populate with real data.
        </p>
      </div>
    )
  }

  const chartData = data?.stages?.map((stage, i) => ({
    label: stage?.label,
    value: stage?.value,
    gradient: [
      { offset: "0%", color: colorFor(i) },
      { offset: "100%", color: colorFor(i + 1) },
    ],
  }))

  return (
    <div className="bg-card border border-border rounded-2xl shadow-sm p-6 mb-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-xl font-bold text-foreground">Product Conversion</h3>
        <select
          value={range}
          onChange={(e) => setRange(e.target.value)}
          className="text-sm bg-background border border-border rounded-md px-2.5 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        >
          {RANGE_OPTIONS?.map((opt) => (
            <option key={opt?.key} value={opt?.key}>
              {opt?.label}
            </option>
          ))}
        </select>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Where commerce meets learning — the full path from visitor to credential, for paid products only.
      </p>

      {!data?.visitorsTracked && (
        <div className="text-xs text-muted-foreground bg-muted rounded-md px-3 py-2 mb-5">
          Visitor tracking isn't wired into every public page yet, so this funnel may start at Product Views.
        </div>
      )}

      <div className="mb-2">
        <FunnelChart data={chartData} orientation="horizontal" layers={3} gap={6} grid={{ bands: true, lines: true }} />
      </div>

      {/* Headline metrics — animated counts + trend, mirroring the reference design */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 mt-6 pt-6 border-t border-border">
        <div className="flex flex-col gap-1">
          <span className="text-sm text-muted-foreground">Purchases</span>
          <div className="flex items-center gap-2">
            <span className="font-mono text-3xl sm:text-4xl font-semibold text-foreground">
              <CountUp end={data?.purchases?.value || 0} duration={1.5} separator="," />
            </span>
            <TrendBadge value={data?.purchases?.change} />
          </div>
          <span className="text-xs text-muted-foreground">vs previous period</span>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-sm text-muted-foreground">Overall Conversion</span>
          <div className="flex items-center gap-2">
            <span className="font-mono text-3xl sm:text-4xl font-semibold text-foreground">
              <CountUp end={data?.overallConversion?.value || 0} duration={1.5} decimals={1} suffix="%" />
            </span>
            <TrendBadge value={data?.overallConversion?.change} />
          </div>
          <span className="text-xs text-muted-foreground">top of funnel → credentials</span>
        </div>
      </div>

      {/* Stage-by-stage metrics with per-period trend */}
      <div className="mt-2">
        {data?.stages?.map((stage, i) => (
          <MetricRow key={stage?.key} label={stage?.label} value={stage?.value} change={stage?.change} index={i} />
        ))}
      </div>
    </div>
  )
}

export default ProductConversionSection